/**
 * filter controller for the ES-backed vacancies overview
 * page. Talks to the WP REST route, receives pre-rendered HTML + facet
 * counts + pagination meta, and swaps them into the DOM.
 *
 * No class or id selectors — every element this script touches is found via
 * a data-js="..." attribute, kept fully separate from styling.
 *
 * URL sync: every filter/search/distance change is reflected in the address
 * bar via history.pushState (see toQueryString/runSearch below), so any
 * filtered state is a shareable, bookmarkable, back/forward-navigable URL —
 * not just in-memory JS state.
 */
(function () {
    'use strict';

    const config = window.piSearchConfig || {};
    const root = document.querySelector('[data-js="vacancies"]');
    if (!root || !config.restUrl) return;

    const filtersEl = root.querySelector('[data-js="filters"]');
    const filtersForm = filtersEl.querySelector('[data-js="filters-form"]');
    const grid = root.querySelector('[data-js="results-grid"]');
    const resultsSection = root.querySelector('[data-js="results"]');
    const totalEl = root.querySelector('[data-js="results-total"]');
    const pagination = root.querySelector('[data-js="pagination"]');
    const loadMoreContainer = root.querySelector('[data-js="load-more-jobs"]');
    const radiusSelect = filtersEl.querySelector('[data-js="radius"]');
    const qInput = filtersEl.querySelector('[data-js="q"]');
    const inputTrigger = filtersEl.querySelector('[data-js="input-trigger"]');
    const addressInput = filtersEl.querySelector('[data-js="address"]');
    const filterTriggers = root.querySelectorAll('[data-js="filter-trigger"]');
    // Coordinates are kept in JS memory only — never exposed in the URL.
    let coordLat = '';
    let coordLon = '';
    const resetBtn = filtersEl.querySelector('[data-js="filters-reset"]');
    const geoBtn = filtersEl.querySelector('[data-js="use-location"]');

    let currentPage = config.initialParams?._page || 1;
    let abortController = null;
    let debounceTimer = null;

    function pushSearchDataLayer(data) {
        window.dataLayer = window.dataLayer || [];
        const address = addressInput ? addressInput.value.trim().toLowerCase() : '';
        window.dataLayer.push({
            event: 'view_search_results',
            search_term: qInput.value.trim(),
            page_type: 'jobspage',
            job_pagetype: 'searchresults',
            job_ids: data.job_ids || [],
            search_results_count: parseInt(data.total, 10) || 0,
            search_location: address,
            search_radius: address ? parseInt(radiusSelect.value, 10) || 0 : 0,
        });
    }

    function syncResetButtonState() {
        if (!resetBtn) return;

        const hasActiveFacet = filtersEl.querySelectorAll('input[type="checkbox"]:checked').length > 0;
        const hasActiveSearch = !!(qInput.value.trim() || (addressInput && addressInput.value.trim()));
        const hasLocation = !!(coordLat || coordLon);
        const hasActiveFilters = hasActiveFacet || hasActiveSearch || hasLocation;
        resetBtn.classList.toggle('filter-active', hasActiveFilters);
        resetBtn.hidden = !hasActiveFilters;
    }

    function readFilterState() {
        const state = {
            q: qInput.value.trim(),
            address: addressInput ? addressInput.value.trim() : '',
            radius_km: radiusSelect.value,
            _page: currentPage,
            per_page: config.initialParams?.per_page || 10,
        };

        // Collect all checked checkboxes — works for any facet added to the template.
        filtersEl.querySelectorAll('input[type="checkbox"]:checked').forEach((el) => {
            if (!state[el.name]) state[el.name] = [];
            state[el.name].push(el.value);
        });

        return state;
    }

    // Coordinates are never put in the URL — they are appended only to the
    // REST API request so the server can do distance filtering.
    function readApiParams() {
        return { ...readFilterState(), lat: coordLat, lon: coordLon, page_id: config.pageId || '' };
    }

    function toQueryString(params) {
        const usp = new URLSearchParams();
        Object.entries(params).forEach(([key, val]) => {
            if (Array.isArray(val)) {
                val.forEach((v) => usp.append(key, v));
            } else if (val !== '' && val !== null && val !== undefined) {
                usp.set(key, val);
            }
        });
        return usp.toString();
    }

    async function runSearch({ pushHistory = true } = {}) {
        const urlParams = readFilterState();
        const apiParams = readApiParams();
        const qs = toQueryString(urlParams);
        const apiQs = toQueryString(apiParams);

        if (abortController) abortController.abort();
        abortController = new AbortController();

        if (pushHistory) {
            const url = `${window.location.pathname}?${qs}`;
            window.history.pushState(urlParams, '', url);
        }

        resultsSection.setAttribute('aria-busy', 'true');
        grid.setAttribute('aria-busy', 'true');

        try {
            const res = await fetch(`${config.restUrl}?${apiQs}`, {
                signal: abortController.signal,
                headers: { 'X-WP-Nonce': config.nonce },
            });

            if (!res.ok) throw new Error(`Search request failed: ${res.status}`);

            const { data } = await res.json();

            grid.innerHTML = data.html;
            const i18n = config.i18n || {};
            const template = data.total === 1
                ? (i18n.resultsTotal || '%s result')
                : (i18n.resultsTotalPlural || '%s results');
            totalEl.innerHTML = template.replace('%s', data.total);
            pagination.innerHTML = data.pagination_html ?? '';
            pagination.dataset.page = data.page;
            pagination.dataset.totalPages = data.total_pages;
            currentPage = data.page;

            if (data.facets) updateFacetCounts(data.facets);

            // Keep load-more in sync with the fresh result set.
            if (loadMoreContainer) {
                loadMoreContainer.innerHTML = data.load_more_html || '';
            }
            pushSearchDataLayer(data);

            grid.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('[proindustry-search]', err);
                grid.textContent = 'Er ging iets mis bij het laden van vacancies. Probeer het opnieuw.';
            }
        } finally {
            resultsSection.setAttribute('aria-busy', 'false');
            grid.setAttribute('aria-busy', 'false');
            syncResetButtonState();
        }
    }

    // Pagination — replaces the full result list and scrolls up.
    pagination.addEventListener('click', (e) => {
        const a = e.target.closest('a[href]');
        if (!a) return;
        e.preventDefault();
        const p = parseInt(new URLSearchParams(new URL(a.href).search).get('_page') || '1', 10);
        currentPage = p;
        runSearch();
    });

    filterTriggers.forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            document.body.classList.toggle('overview__filters--open');
        });
    });

    // Load-more — append the next batch of jobs without replacing the grid.
    if (loadMoreContainer) {
        loadMoreContainer.addEventListener('click', async (e) => {
            const btn = e.target.closest('[data-js="load-more"]');
            if (!btn) return;
            e.preventDefault();

            const nextPage = parseInt(btn.dataset.nextPage || '2', 10);
            btn.setAttribute('aria-busy', 'true');
            btn.style.pointerEvents = 'none';

            const apiParams = { ...readApiParams(), _page: nextPage };
            const apiQs = toQueryString(apiParams);

            try {
                const res = await fetch(`${config.restUrl}?${apiQs}`, {
                    headers: { 'X-WP-Nonce': config.nonce },
                });
                if (!res.ok) throw new Error(`Load more failed: ${res.status}`);

                const { data } = await res.json();

                // Append new cards to the existing grid.
                const tmp = document.createElement('div');
                tmp.innerHTML = data.html || '';
                while (tmp.firstChild) grid.appendChild(tmp.firstChild);

                // Replace the load-more button (or clear it when last page reached).
                loadMoreContainer.innerHTML = data.load_more_html || '';

                // Keep currentPage in sync so filters/pagination stay consistent.
                currentPage = data.page;
                pagination.dataset.page = data.page;
                pagination.dataset.totalPages = data.total_pages;
            } catch (err) {
                console.error('[proindustry-load-more]', err);
                btn.setAttribute('aria-busy', 'false');
                btn.style.pointerEvents = '';
            }
        });
    }


    // Geocoding is handled server-side by QueryBuilder::_getLatLng().
    // When an address is typed, it is sent as-is to the REST endpoint;
    // lat/lon are only set here for the browser geolocation button.
    function geocodeAndSearch() {
        currentPage = 1;
        runSearch();
    }

    function debouncedSearch(delay = 350) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            currentPage = 1;
            runSearch();
        }, delay);
    }

    /**
     * Update the count badges next to each facet checkbox based on the
     * fresh facet data returned by the search endpoint.
     *
     * @param {Object.<string, Array<{key: string, count: number}>>} facets
     */
    function updateFacetCounts(facets) {
        // Reset all counts to 0 first
        filtersEl.querySelectorAll('[data-facet-key]').forEach((input) => {
            const countEl = filtersEl.querySelector(`label[for="${CSS.escape(input.id)}"] [data-facet-count]`);
            if (countEl) countEl.textContent = '(0)';
        });

        // Apply the counts that ES did return.
        Object.entries(facets).forEach(([, options]) => {
            options.forEach(({ key, count }) => {
                const input = filtersEl.querySelector(`[data-facet-key="${CSS.escape(key)}"]`);
                if (!input) return;
                const countEl = filtersEl.querySelector(`label[for="${CSS.escape(input.id)}"] [data-facet-count]`);
                if (countEl) countEl.textContent = `(${count})`;
            });
        });
    }

    // update the results without reload
    filtersForm.addEventListener('submit', (e) => {
        e.preventDefault();
        currentPage = 1;
        runSearch();
    });

    const submitBtn = filtersForm.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.hidden = true;

    filtersEl.addEventListener('change', (e) => {
        if (e.target.matches('input[type="checkbox"]')) {
            syncResetButtonState();
            debouncedSearch(150);
        }
    });
    radiusSelect.addEventListener('change', () => {
        syncResetButtonState();
        geocodeAndSearch();
    });
    if (addressInput) {
        addressInput.addEventListener('input', () => {
            syncResetButtonState();
            // Clear cached coords when the user edits the address field so
            // the next geocode reflects the new value.
            coordLat = '';
            coordLon = '';
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => geocodeAndSearch(), 600);
        });
    }

    const handleQueryInput = () => {
        syncResetButtonState();

        if (qInput.value === '') {
            currentPage = 1;
            clearTimeout(debounceTimer);
            runSearch();
        }
    };

    qInput.addEventListener('input', handleQueryInput);
    qInput.addEventListener('search', handleQueryInput);

    qInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            clearTimeout(debounceTimer);
            currentPage = 1;
            runSearch();
        }
    });

    inputTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        clearTimeout(debounceTimer);
        currentPage = 1;
        runSearch();
    });

    if (resetBtn) {
        resetBtn.addEventListener('click', () => {
            filtersEl.querySelectorAll('input[type="checkbox"]').forEach((el) => (el.checked = false));
            qInput.value = '';
            if (addressInput) addressInput.value = '';
            radiusSelect.value = config.initialParams?.radius_km || '25';
            coordLat = '';
            coordLon = '';
            syncResetButtonState();
            debouncedSearch(0);
        });
    }

    if (geoBtn) {
        geoBtn.addEventListener('click', () => {
            if (!navigator.geolocation) return;
            navigator.geolocation.getCurrentPosition((pos) => {
                coordLat = pos.coords.latitude;
                coordLon = pos.coords.longitude;
                syncResetButtonState();
                debouncedSearch(0);
            });
        });
    }

    syncResetButtonState();

    window.addEventListener('popstate', () => {
        // Restore page from the URL so back/forward navigation lands on the
        // correct page rather than whatever currentPage happens to be in memory.
        currentPage = parseInt(new URLSearchParams(window.location.search).get('_page') || '1', 10);
        runSearch({ pushHistory: false });
    });

    function initFilterTriggerVisibility() {
        const trigger = document.querySelector('[data-js="filter-trigger"]');
        const siteFooter = document.querySelector('.site-footer');

        if (!trigger || !siteFooter) return;

        const reachedClass = 'overview__filter-trigger--footer-reached';

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                trigger.classList.toggle(reachedClass, entry.isIntersecting);
            });
        });

        observer.observe(siteFooter);
    }

    document.addEventListener('DOMContentLoaded', function () {
        initFilterTriggerVisibility();
    });
})();
